import { useEffect, useRef, useState } from "react";
import RfidNav from "./RfidNav";
import {
  DEFAULT_CONFIG,
  READER_TYPES,
  cardStyle,
  getRfidConfig,
  labelStyle,
  saveRfidConfig,
} from "./rfidCommon";

// Configuration du lecteur RFID. Elle est conservée dans ce navigateur et
// utilisée par les pages de transfert et d'inventaire.

const RfidConfiguration = () => {
  const [config, setConfig] = useState(getRfidConfig());
  const [saved, setSaved] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const timersRef = useRef([]);

  useEffect(
    () => () => timersRef.current.forEach((t) => clearTimeout(t)),
    []
  );

  const update = (field, value) => {
    setSaved(false);
    setTestResult(null);
    setConfig((old) => ({ ...old, [field]: value }));
  };

  const onSave = () => {
    saveRfidConfig(config);
    setSaved(true);
    timersRef.current.push(setTimeout(() => setSaved(false), 3000));
  };

  const onReset = () => {
    setConfig(DEFAULT_CONFIG);
    saveRfidConfig(DEFAULT_CONFIG);
    setTestResult(null);
    setSaved(true);
    timersRef.current.push(setTimeout(() => setSaved(false), 3000));
  };

  const onTest = () => {
    setTesting(true);
    setTestResult(null);
    timersRef.current.push(
      setTimeout(() => {
        setTesting(false);
        setTestResult(
          "Connexion simulée réussie : le lecteur répond (puissance " +
            config.power +
            " dBm)."
        );
      }, 1200)
    );
  };

  const isNetwork = config.readerType === "fixed-network";

  return (
    <div className="content-wrapper" style={{ marginTop: "0" }}>
      <section className="content-header">
        <div className="container-fluid">
          <div className="mb-3">
            <h4 className="mb-1">
              <i className="fas fa-cog mr-2"></i>Configuration RFID
            </h4>
            <small className="text-muted">
              Paramétrez le lecteur et le format des étiquettes
            </small>
          </div>
          <RfidNav />
        </div>
      </section>

      <section className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="col-lg-7">
              <div className="card card-outline card-dark" style={cardStyle}>
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fas fa-satellite-dish mr-2"></i>Lecteur
                  </h3>
                </div>
                <div className="card-body">
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label style={labelStyle}>Type de lecteur</label>
                        <select
                          className="form-control"
                          value={config.readerType}
                          onChange={(e) => update("readerType", e.target.value)}
                        >
                          {READER_TYPES.map((t) => (
                            <option key={t.value} value={t.value}>
                              {t.label}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label style={labelStyle}>Nom du lecteur</label>
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Ex : Lecteur dépôt central"
                          value={config.readerName}
                          onChange={(e) => update("readerName", e.target.value)}
                        />
                      </div>
                    </div>
                  </div>

                  {isNetwork && (
                    <div className="row">
                      <div className="col-md-8">
                        <div className="form-group">
                          <label style={labelStyle}>Adresse IP / hôte</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="192.168.1.50"
                            value={config.host}
                            onChange={(e) => update("host", e.target.value)}
                          />
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="form-group">
                          <label style={labelStyle}>Port</label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="5084"
                            value={config.port}
                            onChange={(e) => update("port", e.target.value)}
                          />
                        </div>
                      </div>
                    </div>
                  )}

                  <div className="form-group">
                    <label style={labelStyle}>
                      Puissance d'émission : {config.power} dBm
                    </label>
                    <input
                      type="range"
                      className="custom-range"
                      min="10"
                      max="30"
                      value={config.power}
                      onChange={(e) =>
                        update("power", parseInt(e.target.value, 10))
                      }
                    />
                    <small className="text-muted">
                      Plus la puissance est élevée, plus la portée de lecture est
                      grande.
                    </small>
                  </div>

                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label style={labelStyle}>Cadence de lecture</label>
                        <select
                          className="form-control"
                          value={config.readIntervalMs}
                          onChange={(e) =>
                            update("readIntervalMs", parseInt(e.target.value, 10))
                          }
                        >
                          <option value={400}>Lente</option>
                          <option value={200}>Normale</option>
                          <option value={80}>Rapide</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label style={labelStyle}>
                          Arrêt automatique (secondes)
                        </label>
                        <input
                          type="number"
                          min="0"
                          className="form-control"
                          value={config.autoStopSeconds}
                          onChange={(e) =>
                            update(
                              "autoStopSeconds",
                              Math.max(0, parseInt(e.target.value, 10) || 0)
                            )
                          }
                        />
                        <small className="text-muted">0 = désactivé</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="card card-outline card-dark" style={cardStyle}>
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fas fa-tag mr-2"></i>Étiquettes
                  </h3>
                </div>
                <div className="card-body">
                  <div className="form-group mb-0">
                    <label style={labelStyle}>Format de l'EPC</label>
                    <select
                      className="form-control"
                      value={config.epcFormat}
                      onChange={(e) => update("epcFormat", e.target.value)}
                    >
                      <option value="sgtin96">SGTIN-96 (standard GS1)</option>
                      <option value="free">EPC libre (hexadécimal)</option>
                    </select>
                    <small className="text-muted">
                      Utilisé pour générer les étiquettes dans l'association et
                      les lectures.
                    </small>
                  </div>
                </div>
              </div>

              <div className="d-flex flex-wrap align-items-center">
                <button className="btn btn-dark mr-2 mb-2" onClick={onSave}>
                  <i className="fas fa-save mr-1"></i>
                  Enregistrer
                </button>
                <button
                  className="btn btn-outline-dark mr-2 mb-2"
                  onClick={onTest}
                  disabled={testing}
                >
                  <i
                    className={`fas ${
                      testing ? "fa-spinner fa-spin" : "fa-plug"
                    } mr-1`}
                  ></i>
                  Tester le lecteur
                </button>
                <button
                  className="btn btn-outline-secondary mb-2"
                  onClick={onReset}
                >
                  <i className="fas fa-undo mr-1"></i>
                  Valeurs par défaut
                </button>
                {saved && (
                  <span className="text-success ml-3 mb-2">
                    <i className="fas fa-check mr-1"></i>
                    Configuration enregistrée
                  </span>
                )}
              </div>
              {testResult && (
                <div className="alert alert-success py-2 mt-2">{testResult}</div>
              )}
            </div>

            <div className="col-lg-5">
              <div className="card card-outline card-secondary" style={cardStyle}>
                <div className="card-header">
                  <h3 className="card-title">
                    <i className="fas fa-info-circle mr-2"></i>Modes d'intégration
                  </h3>
                </div>
                <div className="card-body">
                  <p>
                    <strong>Lecteur portable (mode clavier)</strong>
                    <br />
                    <small className="text-muted">
                      Le lecteur saisit les EPC comme un scanner de codes à
                      barre. Idéal pour associer les étiquettes une par une.
                    </small>
                  </p>
                  <p>
                    <strong>Lecteur Android (SDK)</strong>
                    <br />
                    <small className="text-muted">
                      Une application mobile lit les étiquettes en masse et les
                      envoie à l'ERP. Adapté aux inventaires.
                    </small>
                  </p>
                  <p className="mb-0">
                    <strong>Lecteur fixe / portique</strong>
                    <br />
                    <small className="text-muted">
                      Un lecteur réseau pousse les lectures vers l'ERP en continu
                      (réception, sortie de dépôt).
                    </small>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RfidConfiguration;
